import React, { useCallback, useState, useMemo } from 'react';
import { CellProps } from 'react-table';
import { useSelector } from '../hook-utils';
import { Identifier, QueueIconEnum, QueueStats } from '../model';
import { selectMyAccess, selectMyself } from '../store/reducers/config';
import { selectSelectedQueueView, ColumnWidths, selectGridColumnWidths } from '../store/reducers/localSettings';
import { selectQueuesInView, selectQueueViewIsLoading } from '../store/reducers/queues';
import { TableColumn } from './FlexTable';
import { MenuTarget, targetFromEvent } from './Menu';
import MoreButton from './MoreButton';
import QualityIndicator from './QualityIndicator';
import QueueIcon from './QueueIcon';
import { QueueMenu, QueueMenuState } from './QueueMenu';
import { QueuesPageTable } from './QueuesPage';
import './QueuesPage.scss';
import './QueuesTable.scss';
import Timer from './Timer';

const numericColumn: Partial<TableColumn<QueueStats>> = {
  className: 'numeric',
  headerClassName: 'numeric',
  width: 90,
}

function Columns(onMore: (e: React.SyntheticEvent, queue: QueueStats, target: MenuTarget) => void, myId: Identifier, columnsWidths?: ColumnWidths, extensionViewOtherVoicemail?: boolean): Array<TableColumn<QueueStats>> {
  let columns: TableColumn<QueueStats>[] = [{
    id: 'icon',
    width: 42,
    disableResizing: true,
    accessor: q => q.icon,
    Cell: ({ cell }: CellProps<QueueStats, QueueIconEnum>) => <QueueIcon icon={cell.value} />,
  }]

  if (extensionViewOtherVoicemail) {
    columns = [...columns, {
      id: 'voicemail',
      width: 20,
      disableResizing: true,
      accessor: q => {
        return q.voicemailCount > 0 ? <div className='count' title={q.voicemailCount + ' voicemail' + (q.voicemailCount > 1 ? 's' : '')}>{q.voicemailCount}</div> : null
      },
      disableSortBy: true,
    }]
  }

  columns = [...columns,
  {
    id: 'queue',
    Header: 'Queue',
    accessor: q => q.id,
    width: 80,
  }, {
    id: 'description',
    Header: 'Description',
    accessor: q => q.description,
  }, {
    id: 'mode',
    Header: 'Mode',
    accessor: q => q.mode === 'auto' ? 'Auto' : 'Manual',
    width: 80,
  }, {
    id: 'status',
    Header: 'Status',
    accessor: q => q.status,
    Cell: ({ cell }: CellProps<QueueStats, string>) => <span title={cell.value}>{cell.value}</span>,
  }, {
    ...numericColumn,
    id: 'count',
    Header: 'Q Count',
    accessor: q => q.count,
  }, {
    ...numericColumn,
    id: 'time',
    Header: 'Q Time',
    accessor: q => q.time,
    width: 120,
    Cell: ({ cell }: CellProps<QueueStats, number | null>) => cell.value ? <Timer ts={cell.value} /> : '00:00',
  }, {
    ...numericColumn,
    id: 'agents',
    Header: 'Agents',
    accessor: q => q.agents,
  }, {
    ...numericColumn,
    id: 'answer',
    Header: 'Answer',
    accessor: q => q.answer,
  }, {
    ...numericColumn,
    id: 'abandoned',
    Header: 'Abandoned',
    accessor: q => q.abandoned,
  }, {
    ...numericColumn,
    id: 'other',
    Header: 'Other',
    accessor: q => q.other,
  }, {
    id: 'agentStatus',
    Header: 'Service level',
    accessor: q => q.agentStatus,
    Cell: ({ cell }: CellProps<QueueStats, number>) => <QualityIndicator value={cell.value} queueId={cell.row.original.id} />,
    width: 100,
    disableResizing: true,
  }, {
    id: 'menuButton',
    accessor: q => <MoreButton className='menu-button' onClick={e => onMore(e, q, e.currentTarget)} />,
    width: 35,
    disableResizing: true,
    disableSortBy: true,
  }]

  return columns.map(x => {
    if (x.id && columnsWidths && columnsWidths[x.id]) {
      return {
        ...x,
        width: columnsWidths[x.id],
      }
    }
    return x
  })
}

export default function QueuesTable() {
  const view = useSelector(selectSelectedQueueView)
  const loading = useSelector(s => selectQueueViewIsLoading(s, view))
  const queues = useSelector(selectQueuesInView)
  const myId = useSelector(selectMyself)
  const access = useSelector(selectMyAccess)
  const widths = useSelector(s => selectGridColumnWidths(s, 'queues'))

  const [menuState, setMenuState] = useState<QueueMenuState>({})

  const onMore = useCallback((e: React.SyntheticEvent, queue: QueueStats, target: MenuTarget) => {
    e.preventDefault()
    e.stopPropagation()
    setMenuState({ target, queue })
  }, [])

  const expandable = useCallback((queue: QueueStats) => !queue.virtual, [])

  const onContextMenu = useCallback((e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, queue: QueueStats) => {
    setMenuState({ target: targetFromEvent(e), queue })
  }, [])

  const columns = useMemo(() => {
    return myId ? Columns(onMore, myId, widths, access.features.extensionViewOtherVoicemail) : []
  }, [access.features.extensionViewOtherVoicemail, myId, onMore, widths])

  return <>
    <QueuesPageTable className='QueuesTable' data={queues}
      onContextMenu={onContextMenu}
      columns={columns}
      loading={loading}
      expandable={expandable} getRowClass={getRowClass} />
    <QueueMenu onClose={() => setMenuState({})} {...menuState} />
  </>
}

function getRowClass(queue: QueueStats) {
  if (queue.icon === QueueIconEnum.OpenWarning) {
    return 'warning'
  } else if (queue.icon === QueueIconEnum.OpenAlert) {
    return 'alert'
  }
}
